import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { SlCloudUpload } from 'react-icons/sl';
import Swal from 'sweetalert2';
import { LoadingButton } from '@mui/lab';
import dayjs from 'dayjs';
import InfoTooltip from '../InfoTooltip';
import {
  createCategory,
  createDoorList,
  createSpecialAccesss,
  getBnbList,
  updateCleaner,
  updateContractor,
  updateContractTemplates,
  updateSpecialAccess,
  uploadFile,
} from '../../helper/backend_helper';

const CONTRACT_TEMPLATE_VARIABLES = [
  { token: '[OWNER]', description: 'Owner details from the owner information step.' },
  { token: '[PROPERTIES]', description: 'Property details collected in the property step.' },
  { token: '[BANKDETAILS]', description: 'Bank and payment details from the payment step.' },
  { token: '[SIGNATURESAMPLE]', description: 'The owner signature image captured digitally.' },
  { token: '[COMMISSION%]', description: 'The selected contract commission percentage.' },
  { token: '[CONTRACTDURATION]', description: 'The selected contract duration.' },
  { token: '[CANCELLATIONNOTICE]', description: 'The selected contract cancellation notice.' },
  { token: '[DATE]', description: 'The date when the contract PDF is generated.' },
];

const TemplateVariables = () => (
  <Box sx={{ mt: 2, p: 2, border: '1px solid #E5E7EB', borderRadius: 1, bgcolor: '#FAFAFA' }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>Available variables</Typography>
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
      {CONTRACT_TEMPLATE_VARIABLES.map((item) => (
        <Box key={item.token} sx={{ p: 1, border: '1px solid #E5E7EB', borderRadius: 1, bgcolor: '#fff', minWidth: 220 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{item.token}</Typography>
          <Typography variant="caption" color="text.secondary">{item.description}</Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);


const UpdateContractTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = location?.state?.userData;
  const [name, setName] = useState(userData?.name);
  const [template, setTemplate] = useState(userData?.template);
  const [mode, setMode] = useState(userData?.mode || 'standard');
  const handleChange = (content) => {
    setTemplate(content);
  };
  const [loading, setLoading] = useState(false);

  const formats = ["header","bold","italic","underline","strike","blockquote",
    "list","bullet","indent","link","color","clean",
  ];
  const modules = {
    toolbar: {
      container: [
        [{ header: [2, 3, 4, false] }],
        ["bold", "italic", "underline", "blockquote"],
        [{ color: [] }],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        ["link"],
        ["clean"],
      ],
      // handlers: {
      //   image: imageHandler,
      // },
    },
    clipboard: {
      matchVisual: true,
    },
  }
  
  const handleSave = async () => {
    try {
      setLoading(true);

      if (!name) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Name is required',
        });
        setLoading(false);
        return;
      }
      if (!template) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Template is required',
        });
        setLoading(false);
        return;
      }

      // Prepare headers for multipart form data

      const Categorydata = {
        name,
        template,
        mode,
        isDefault: userData?.isDefault || false,
      };

      const Categoryheaders = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const response = await updateContractTemplates(userData?._id, Categorydata, Categoryheaders);

      Swal.fire({
        icon: 'success',
        title: 'Contract Template updated successfully',
      });

      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `${error?.response?.data?.errors[0]?.message}`,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title> Update Contract Template </title>
      </Helmet>
      <Card sx={{ padding: 3 }}>
        <Stack
          onClick={() => navigate(-1)}
          gap={3}
          sx={{ cursor: 'pointer', mb: 3 }}
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
        >
          <FaArrowLeftLong size={24} />
          <Typography variant="h4">Update Contract Template</Typography>
        </Stack>

        <Box component="form" sx={{ width: '100%' }}>
          <Stack direction="row" useFlexGap flexWrap="wrap">
            <Stack sx={{ width: { xs: '100%', md: '48%' }, mb: 3 }}>
              <FormLabel sx={{ mb: 1 }}>Template Mode <InfoTooltip title="Which contract variant this template covers — Standard or All Inclusive — so the right one is used per owner link." /></FormLabel>
              <FormControl fullWidth>
                <Select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="allInclusive">All Inclusive</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          <Stack direction="row" useFlexGap flexWrap="wrap">
            <Stack sx={{ width: '100%' }}>
              <FormLabel sx={{ mb: 1 }}>Name <InfoTooltip title="Internal label to identify this contract template in the templates list." /></FormLabel>
              <TextField
                fullWidth
                label="Enter Name"
                id="outlined-size-small"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Stack>
          </Stack>

          {/* <Stack mt={4} direction="row" useFlexGap flexWrap="wrap">
            <Stack sx={{ width: '100%' }}>
              <FormLabel sx={{ mb: 1 }}>Template</FormLabel>
              <TextField
                fullWidth
                multiline
                rows={20}
                label="Enter Format"
                id="outlined-size-small"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                sx={{
                  '& .MuiInputBase-root textarea': {
                    resize: 'vertical',
                    overflow: 'auto',
                  },
                }}
              />
            </Stack>
          </Stack> */}
           <Stack mt={4} direction="row" useFlexGap flexWrap="wrap">
                      <Stack sx={{ width: '100%' }}>
                        <FormLabel sx={{ mb: 1 }}>Template <InfoTooltip title="The contract body. Insert variables like [OWNER] or [DATE] to auto-fill owner and booking data in the generated PDF." /></FormLabel>
                        <ReactQuill
                          theme="snow"
                          value={template}
                          onChange={handleChange}
                          style={{
                            height: '400px',
                            marginBottom: '50px',
                            backgroundColor: '#fff',
                          }}
                          // modules={modules}
                          // formats={formats}
                        />
                        <TemplateVariables />
                      </Stack>
                    </Stack>
        </Box>
        <LoadingButton
          sx={{
            width: 100,
            mt: 4,
            mb: 4,
            boxShadow: 'none',
            backgroundColor: '#1E3A5F',
            '&:hover': {
              backgroundColor: '#1E3A5F',
              boxShadow: 'none',
            },
          }}
          variant="contained"
          onClick={handleSave}
          loading={loading}
        >
          Update
        </LoadingButton>
      </Card>
    </>
  );
};

export default UpdateContractTemplate;
